import React, { useState, useEffect, useRef, useMemo, Suspense } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, Html, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { 
  Box, Upload, Trash2, CheckCircle2, AlertCircle, RefreshCw, 
  ExternalLink, Sparkles, Sliders, FileText, Check, ShieldCheck, Info,
  Truck, Users, Wrench, Building2, Edit3, X, Copy, Eye, RotateCw,
  Download, Ruler
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: '📁 ทั้งหมด', icon: Box, color: '#6366f1' },
  { id: 'reach_truck', label: '🚜 รถยกสูง (Reach Truck)', icon: Truck, color: '#0284c7', defaultHeight: '2.30' },
  { id: 'forklift', label: '🚚 รถโฟล์คลิฟต์ (Forklift)', icon: Truck, color: '#ea580c', defaultHeight: '2.10' },
  { id: 'operator', label: '👷 พนักงาน (Operators)', icon: Users, color: '#eab308', defaultHeight: '1.70' },
  { id: 'equipment', label: '🏗️ เครื่องจักร & อุปกรณ์', icon: Wrench, color: '#10b981', defaultHeight: '1.80' },
  { id: 'other', label: '📦 อื่นๆ (3D Asset)', icon: Box, color: '#8b5cf6', defaultHeight: '1.50' }
];

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// -------------------------------------------------------------
// Error boundary for 3D canvas
// -------------------------------------------------------------
class ModelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(err) {
    console.warn("3D Preview Error:", err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-slate-50 z-20">
          <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-500 flex items-center justify-center mb-3 shadow-xs">
            <AlertCircle size={26} />
          </div>
          <div className="font-bold text-sm text-slate-800">ไม่สามารถเรนเดอร์โมเดล 3D ได้</div>
          <div className="text-xs text-slate-500 mt-1 max-w-sm">
            {this.state.error?.message || 'ไฟล์โมเดลอาจมีข้อผิดพลาด หรือลิงก์ไฟล์ไม่พร้อมใช้งาน'}
          </div>
          {this.props.onRetry && (
            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, error: null });
                this.props.onRetry();
              }}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-colors cursor-pointer"
            >
              ลองโหลดใหม่อีกครั้ง
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// -------------------------------------------------------------
// Loading spinner inside Canvas
// -------------------------------------------------------------
function ModelLoadingIndicator() {
  return (
    <Html center>
      <div className="flex flex-col items-center justify-center p-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-indigo-100 text-indigo-700 min-w-[200px]">
        <RefreshCw size={28} className="animate-spin mb-2 text-indigo-600" />
        <span className="font-bold text-xs text-slate-800">กำลังโหลดโมเดล 3D...</span>
        <span className="text-[10px] text-slate-400 mt-0.5">ประมวลผล Mesh และสัดส่วนโมเดล</span>
      </div>
    </Html>
  );
}

// -------------------------------------------------------------
// Camera Controller for Presets
// -------------------------------------------------------------
function CameraController({ cameraCommand, controlsRef }) {
  const { camera } = useThree();

  useEffect(() => {
    if (!cameraCommand || !controlsRef.current) return;
    const ctrl = controlsRef.current;
    const { view } = cameraCommand;

    if (view === 'iso') {
      camera.position.set(2.4, 1.8, 2.8);
      ctrl.target.set(0, 0.8, 0);
    } else if (view === 'front') {
      camera.position.set(0, 0.9, 3.4);
      ctrl.target.set(0, 0.8, 0);
    } else if (view === 'side') {
      camera.position.set(3.4, 0.9, 0);
      ctrl.target.set(0, 0.8, 0);
    } else if (view === 'top') {
      camera.position.set(0, 4.2, 0.001);
      ctrl.target.set(0, 0, 0);
    }
    ctrl.update();
  }, [cameraCommand, camera, controlsRef]);

  return null;
}

// -------------------------------------------------------------
// GLB Preview Mesh loader
// -------------------------------------------------------------
function GLBPreviewMesh({ url, targetHeight = 1.70, showBox = true, onModelLoaded }) {
  const { scene } = useGLTF(url);

  const { clonedScene, boxBounds, info } = useMemo(() => {
    if (!scene) return { clonedScene: null, boxBounds: null, info: null };
    const clone = scene.clone(true);

    // Compute original bounding box
    const origBox = new THREE.Box3().setFromObject(clone);
    const origSize = new THREE.Vector3();
    origBox.getSize(origSize);

    // Scale so that model height (Y axis) matches targetHeight
    const origH = origSize.y;
    const scale = (origH > 0.0001) ? (targetHeight / origH) : 1;
    clone.scale.set(scale, scale, scale);

    // Recompute scaled box
    const scaledBox = new THREE.Box3().setFromObject(clone);
    const scaledCenter = new THREE.Vector3();
    const scaledSize = new THREE.Vector3();
    scaledBox.getCenter(scaledCenter);
    scaledBox.getSize(scaledSize);

    // Center horizontal at (0, 0) and bottom at Y = 0
    clone.position.x = -scaledCenter.x;
    clone.position.z = -scaledCenter.z;
    clone.position.y = -scaledBox.min.y;

    clone.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    const modelInfo = {
      widthMm: Math.round(scaledSize.x * 1000),
      heightMm: Math.round(scaledSize.y * 1000),
      lengthMm: Math.round(scaledSize.z * 1000),
      scaleRatio: scale.toFixed(3),
      origHeightMm: Math.round(origH * 1000),
      origWidthMm: Math.round(origSize.x * 1000),
      origLengthMm: Math.round(origSize.z * 1000)
    };

    return {
      clonedScene: clone,
      boxBounds: {
        center: [0, scaledSize.y / 2, 0],
        size: [scaledSize.x, scaledSize.y, scaledSize.z]
      },
      info: modelInfo
    };
  }, [scene, targetHeight]);

  useEffect(() => {
    if (info && onModelLoaded) {
      onModelLoaded(info);
    }
  }, [info, onModelLoaded]);

  return (
    <group>
      {clonedScene && <primitive object={clonedScene} />}

      {showBox && boxBounds && (
        <mesh position={boxBounds.center}>
          <boxGeometry args={boxBounds.size} />
          <meshBasicMaterial color="#6366f1" wireframe transparent opacity={0.4} />
        </mesh>
      )}
    </group>
  );
}

// -------------------------------------------------------------
// Interactive 3D Model Preview Modal
// -------------------------------------------------------------
function Model3DPreviewModal({ model, onClose, onUpdateHeight }) {
  const initialH = parseFloat(model.target_height_m) || 1.70;
  const [currentHeight, setCurrentHeight] = useState(initialH);
  const [heightInput, setHeightInput] = useState(initialH.toFixed(2));
  const [showBox, setShowBox] = useState(true);
  const [autoRotate, setAutoRotate] = useState(false);
  const [cameraCommand, setCameraCommand] = useState({ view: 'iso', id: 1 });
  const [measuredDims, setMeasuredDims] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const controlsRef = useRef(null);

  const updateHeight = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (!isNaN(num)) {
      const clamped = Math.max(0.01, num);
      setCurrentHeight(clamped);
      setHeightInput(clamped.toFixed(2));
    }
  };

  const catInfo = CATEGORIES.find(c => c.id === model.category) || CATEGORIES[CATEGORIES.length - 1];
  const IconComp = catInfo.icon || Box;

  const handleSaveHeight = async () => {
    if (!onUpdateHeight) return;
    setIsSaving(true);
    const ok = await onUpdateHeight(model.id, currentHeight);
    setIsSaving(false);
    if (ok) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    }
  };

  // Keyboard shortcut: Esc to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-5xl h-[92vh] max-h-[900px] flex flex-col overflow-hidden relative">
        {/* Modal Top Bar */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-white z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 shadow-xs" style={{
              backgroundColor: `${catInfo.color}18`,
            }}>
              <IconComp size={22} style={{ color: catInfo.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-slate-900 text-sm truncate">
                  {model.name}
                </h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0" style={{
                  backgroundColor: `${catInfo.color}15`,
                  color: catInfo.color,
                  borderColor: `${catInfo.color}30`
                }}>
                  {catInfo.label}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                <span>{model.file_name}</span>
                <span>•</span>
                <span>{formatFileSize(model.file_size)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {model.file_url && (
              <a
                href={model.file_url}
                download={model.file_name}
                className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs"
                title="ดาวน์โหลดไฟล์โมเดล .glb"
              >
                <Download size={13} className="text-slate-500" />
                <span className="hidden sm:inline">ดาวน์โหลด .glb</span>
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              title="ปิดหน้าต่างพรีวิว (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* 3D Canvas Viewport */}
        <div className="relative flex-1 bg-gradient-to-b from-slate-100/80 via-slate-50 to-slate-100/50 overflow-hidden select-none">
          {/* Top-Left Floating Specs Panel */}
          <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-md rounded-2xl p-3 shadow-lg border border-slate-200/80 text-xs space-y-2 max-w-xs">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
              <span className="font-bold text-slate-800 flex items-center gap-1.5 text-[11px]">
                <Ruler size={13} className="text-indigo-600" />
                <span>สัดส่วนจริงจำลองในฉาก (3D)</span>
              </span>
              <button
                type="button"
                onClick={() => setShowBox(!showBox)}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition-colors cursor-pointer ${
                  showBox
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                }`}
              >
                {showBox ? '✓ Bounding Box' : 'ซ่อนกรอบ'}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1.5 text-center">
              <div className="bg-slate-50 p-1.5 rounded-xl border border-slate-100">
                <span className="text-[10px] text-slate-400 block font-semibold">กว้าง (X)</span>
                <span className="font-bold text-slate-800 text-xs">
                  {measuredDims?.widthMm ? `${(measuredDims.widthMm / 10).toFixed(0)} cm` : '...'}
                </span>
                <span className="text-[9px] text-slate-400 block">
                  {measuredDims?.widthMm ? `${measuredDims.widthMm.toLocaleString()} mm` : ''}
                </span>
              </div>
              <div className="bg-slate-50 p-1.5 rounded-xl border border-slate-100">
                <span className="text-[10px] text-slate-400 block font-semibold">ยาว (Z)</span>
                <span className="font-bold text-slate-800 text-xs">
                  {measuredDims?.lengthMm ? `${(measuredDims.lengthMm / 10).toFixed(0)} cm` : '...'}
                </span>
                <span className="text-[9px] text-slate-400 block">
                  {measuredDims?.lengthMm ? `${measuredDims.lengthMm.toLocaleString()} mm` : ''}
                </span>
              </div>
              <div className="bg-indigo-50/70 p-1.5 rounded-xl border border-indigo-100">
                <span className="text-[10px] text-indigo-500 block font-semibold">สูง (Y)</span>
                <span className="font-extrabold text-indigo-700 text-xs">
                  {measuredDims?.heightMm ? `${(measuredDims.heightMm / 10).toFixed(0)} cm` : '...'}
                </span>
                <span className="text-[9px] text-indigo-400 block">
                  {measuredDims?.heightMm ? `${measuredDims.heightMm.toLocaleString()} mm` : ''}
                </span>
              </div>
            </div>

            {measuredDims?.scaleRatio && (
              <div className="text-[10px] text-slate-400 flex items-center justify-between pt-0.5">
                <span>สเกลต้นฉบับ: {(measuredDims.origHeightMm / 1000).toFixed(2)} ม.</span>
                <span className="font-bold text-indigo-600">อัตราส่วน {measuredDims.scaleRatio}x</span>
              </div>
            )}
          </div>

          {/* Top-Right Camera Controls Toolbar */}
          <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-2">
            <div className="bg-white/90 backdrop-blur-md rounded-2xl p-1.5 shadow-lg border border-slate-200/80 flex items-center gap-1 text-xs">
              <button
                type="button"
                onClick={() => setCameraCommand({ view: 'iso', id: Date.now() })}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  cameraCommand.view === 'iso' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="มุมมอง Isometric 3D"
              >
                🌐 Isometric
              </button>
              <button
                type="button"
                onClick={() => setCameraCommand({ view: 'front', id: Date.now() })}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  cameraCommand.view === 'front' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="มุมมองหน้าตรง Front"
              >
                ⏹️ หน้าตรง
              </button>
              <button
                type="button"
                onClick={() => setCameraCommand({ view: 'side', id: Date.now() })}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  cameraCommand.view === 'side' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="มุมมองด้านข้าง Side"
              >
                ↔️ ด้านข้าง
              </button>
              <button
                type="button"
                onClick={() => setCameraCommand({ view: 'top', id: Date.now() })}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  cameraCommand.view === 'top' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="มุมมองจากด้านบน Top 2D"
              >
                🔝 ด้านบน
              </button>
              <span className="w-px h-5 bg-slate-200 mx-0.5" />
              <button
                type="button"
                onClick={() => setAutoRotate(!autoRotate)}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                  autoRotate
                    ? 'bg-amber-500 text-white shadow-xs animate-pulse'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="เปิด/ปิดการหมุน 360 องศาอัตโนมัติ"
              >
                <RotateCw size={12} className={autoRotate ? 'animate-spin' : ''} />
                <span>360°</span>
              </button>
            </div>
          </div>

          {/* Bottom-Center Interactive Hint */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <div className="bg-slate-900/75 backdrop-blur-md text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium shadow-lg flex items-center gap-2 border border-white/10">
              <span>💡 คลิกซ้าย: หมุน (Orbit)</span>
              <span className="opacity-40">•</span>
              <span>คลิกขวา: เลื่อน (Pan)</span>
              <span className="opacity-40">•</span>
              <span>ลูกกลิ้ง: ซูม (Zoom)</span>
            </div>
          </div>

          {/* Actual 3D Canvas */}
          <ModelErrorBoundary key={retryKey} onRetry={() => setRetryKey(k => k + 1)}>
            <Canvas
              shadows
              gl={{ alpha: true, antialias: true }}
              camera={{ position: [2.4, 1.8, 2.8], fov: 45, near: 0.05, far: 100 }}
              style={{ width: '100%', height: '100%' }}
            >
              <ambientLight intensity={0.8} />
              <directionalLight
                position={[6, 10, 6]}
                intensity={1.2}
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
              />
              <directionalLight position={[-6, 6, -6]} intensity={0.4} />
              <pointLight position={[0, -2, 0]} intensity={0.2} />

              <Suspense fallback={<ModelLoadingIndicator />}>
                <GLBPreviewMesh
                  url={model.file_url}
                  targetHeight={currentHeight}
                  showBox={showBox}
                  onModelLoaded={setMeasuredDims}
                />
              </Suspense>

              {/* Studio Ground Grid & Shadows */}
              <Grid
                position={[0, 0, 0]}
                infiniteGrid
                cellSize={0.25}
                sectionSize={1.0}
                sectionColor="#6366f1"
                cellColor="#cbd5e1"
                fadeDistance={15}
                fadeStrength={1.2}
              />
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.001, 0]} receiveShadow>
                <planeGeometry args={[50, 50]} />
                <shadowMaterial opacity={0.25} />
              </mesh>

              <OrbitControls
                ref={controlsRef}
                makeDefault
                autoRotate={autoRotate}
                autoRotateSpeed={1.8}
                minDistance={0.4}
                maxDistance={15}
                maxPolarAngle={Math.PI / 2 + 0.02}
              />
              <CameraController cameraCommand={cameraCommand} controlsRef={controlsRef} />
            </Canvas>
          </ModelErrorBoundary>
        </div>

        {/* Modal Bottom Toolbar: Interactive Scale Adjustment & Quick Save */}
        <div className="px-6 py-3.5 bg-white border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs z-10">
          <div className="flex items-center gap-3 flex-1 min-w-[280px]">
            <span className="font-bold text-slate-700 shrink-0 flex items-center gap-1.5">
              <Sliders size={14} className="text-indigo-600" />
              <span>ปรับสเกลความสูงจำลอง:</span>
            </span>
            <input
              type="range"
              min="0.1"
              max="6.0"
              step="0.01"
              value={Math.min(6.0, Math.max(0.1, currentHeight))}
              onChange={e => updateHeight(parseFloat(e.target.value))}
              className="flex-1 accent-indigo-600 cursor-pointer h-2 bg-slate-200 rounded-lg"
            />
            {/* Direct Editable Height Input Box */}
            <div className="relative flex items-center shrink-0">
              <input
                type="number"
                min="0.1"
                max="20.0"
                step="0.01"
                value={heightInput}
                onChange={e => {
                  const valStr = e.target.value;
                  setHeightInput(valStr);
                  const num = parseFloat(valStr);
                  if (!isNaN(num) && num >= 0.05) {
                    setCurrentHeight(num);
                  }
                }}
                onBlur={() => {
                  let num = parseFloat(heightInput);
                  if (isNaN(num) || num < 0.1) num = 0.1;
                  if (num > 20.0) num = 20.0;
                  setCurrentHeight(num);
                  setHeightInput(num.toFixed(2));
                }}
                className="w-24 pl-3 pr-7 py-1 bg-indigo-50 border-2 border-indigo-300 hover:border-indigo-400 focus:border-indigo-600 focus:bg-white rounded-xl text-xs font-extrabold text-indigo-800 outline-none text-center transition-all shadow-inner"
                title="กรอกตัวเลขความสูงที่ต้องการได้โดยตรง (เมตร)"
              />
              <span className="absolute right-2.5 text-[11px] font-bold text-indigo-500 pointer-events-none">ม.</span>
            </div>
          </div>

          {/* Quick Height Preset Buttons & Save */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => updateHeight(0.30)}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-[11px] transition-colors cursor-pointer"
              title="สเกลอุปกรณ์วางพื้น / พาเลทไกด์ (0.30 ม.)"
            >
              0.30ม.
            </button>
            <button
              type="button"
              onClick={() => updateHeight(1.70)}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-[11px] transition-colors cursor-pointer"
            >
              1.70ม. (คน)
            </button>
            <button
              type="button"
              onClick={() => updateHeight(2.10)}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-[11px] transition-colors cursor-pointer"
            >
              2.10ม. (Forklift)
            </button>
            <button
              type="button"
              onClick={() => updateHeight(2.30)}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-[11px] transition-colors cursor-pointer"
            >
              2.30ม. (Reach Truck)
            </button>

            {currentHeight !== (parseFloat(model.target_height_m) || 1.70) && (
              <button
                type="button"
                onClick={handleSaveHeight}
                disabled={isSaving}
                className="ml-2 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
              >
                {isSaving ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : saveSuccess ? (
                  <Check size={12} className="text-emerald-300" />
                ) : (
                  <CheckCircle2 size={12} />
                )}
                <span>{saveSuccess ? 'บันทึกแล้ว!' : 'บันทึกสเกลนี้'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Model3DManager() {
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  
  // Upload form state
  const [modelName, setModelName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('reach_truck');
  const [targetHeightM, setTargetHeightM] = useState('2.30');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Edit modal state
  const [editingModel, setEditingModel] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // 3D Preview modal state
  const [previewModel, setPreviewModel] = useState(null);

  const fetchModels = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/models-3d');
      const json = await res.json();
      if (json.status === 'success') {
        setModels(json.data || []);
      }
    } catch (err) {
      console.error('Failed to load 3D models:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const handleCategoryChange = (catId) => {
    setUploadCategory(catId);
    const catObj = CATEGORIES.find(c => c.id === catId);
    if (catObj && catObj.defaultHeight) {
      setTargetHeightM(catObj.defaultHeight);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!modelName) {
        const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
        setModelName(cleanName);
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setUploadError('กรุณาเลือกไฟล์ 3D ก่อนอัปโหลด (.glb, .gltf, .fbx, .obj)');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess('');

    const formData = new FormData();
    formData.append('model_file', selectedFile);
    formData.append('name', modelName.trim() || selectedFile.name);
    formData.append('category', uploadCategory);
    formData.append('target_height_m', targetHeightM || '1.70');
    formData.append('description', description.trim());

    try {
      const res = await fetch('/api/models-3d/upload', {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (json.status === 'success') {
        setUploadSuccess(`อัปโหลดโมเดล "${modelName || selectedFile.name}" สำเร็จ! สามารถนำไปวางในผังได้ทันที`);
        setSelectedFile(null);
        setModelName('');
        setDescription('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchModels();
      } else {
        setUploadError(json.message || 'เกิดข้อผิดพลาดในการอัปโหลด');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingModel) return;
    setIsSavingEdit(true);

    try {
      const res = await fetch(`/api/models-3d/${editingModel.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingModel.name,
          category: editingModel.category,
          target_height_m: parseFloat(editingModel.target_height_m) || 1.70,
          description: editingModel.description || ''
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setEditingModel(null);
        fetchModels();
      } else {
        alert(json.message || 'Failed to update');
      }
    } catch (err) {
      console.error('Update error:', err);
      alert('Failed to update model');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleSetActive = async (modelId) => {
    try {
      const res = await fetch(`/api/models-3d/${modelId}/set-active`, {
        method: 'PUT',
      });
      const json = await res.json();
      if (json.status === 'success') {
        fetchModels();
      } else {
        alert(json.message || 'Failed to set active');
      }
    } catch (err) {
      console.error('Set active error:', err);
    }
  };

  const handleRevertToDefault = async (category) => {
    try {
      const res = await fetch(`/api/models-3d/deactivate-category?category=${category}`, {
        method: 'PUT',
      });
      const json = await res.json();
      if (json.status === 'success') {
        fetchModels();
      }
    } catch (err) {
      console.error('Revert to default error:', err);
    }
  };

  const handleUpdateHeight = async (modelId, newHeight) => {
    const targetModel = models.find(m => m.id === modelId);
    if (!targetModel) return false;
    try {
      const res = await fetch(`/api/models-3d/${modelId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: targetModel.name,
          category: targetModel.category,
          target_height_m: parseFloat(newHeight),
          description: targetModel.description || ''
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        setModels(prev => prev.map(m => m.id === modelId ? { ...m, target_height_m: String(newHeight) } : m));
        if (previewModel && previewModel.id === modelId) {
          setPreviewModel(prev => ({ ...prev, target_height_m: String(newHeight) }));
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to update height:', err);
      return false;
    }
  };

  const handleDelete = async (modelId, name) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบโมเดล 3D "${name}"?`)) return;

    try {
      const res = await fetch(`/api/models-3d/${modelId}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (json.status === 'success') {
        if (previewModel && previewModel.id === modelId) {
          setPreviewModel(null);
        }
        fetchModels();
      } else {
        alert(json.message || 'Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filteredModels = models.filter(m => selectedCategory === 'all' || m.category === selectedCategory);



  const getCategoryInfo = (catId) => {
    return CATEGORIES.find(c => c.id === catId) || { label: catId, color: '#64748b', icon: Box };
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white rounded-3xl p-6 shadow-md border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="p-2 bg-indigo-500/20 text-indigo-400 rounded-xl border border-indigo-400/30">
              <Box size={22} />
            </span>
            <h2 className="text-xl font-extrabold tracking-tight">3D Model Asset Library</h2>
            <span className="text-[11px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30 px-2 py-0.5 rounded-full">
              Admin & Warehouse Twin
            </span>
          </div>
          <p className="text-xs text-slate-300 max-w-3xl leading-relaxed">
            คลังจัดการไฟล์โมเดล 3 มิติสำหรับผังโรงงานและคลังสินค้า (Digital Twin Layout) รองรับโมเดลคน รถยก Reach Truck, Forklift, เครื่องจักร และอุปกรณ์คลังสินค้า ระบบมี Auto-Scaling ปรับขนาดความสูงและติดพื้นให้อัตโนมัติ
          </p>
        </div>

        <button
          type="button"
          onClick={fetchModels}
          className="self-start md:self-auto px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-all border border-white/10 shadow-xs cursor-pointer"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          <span>รีเฟรชข้อมูล ({models.length})</span>
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-gray-200">
        {CATEGORIES.map(tab => {
          const count = tab.id === 'all' ? models.length : models.filter(m => m.category === tab.id).length;
          const isSelected = selectedCategory === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedCategory(tab.id)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-2 cursor-pointer ${
                isSelected
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                isSelected ? 'bg-indigo-800 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Upload Form & Quick Guide */}
        <div className="lg:col-span-1 space-y-4">
          {/* Upload Card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <Upload size={16} className="text-indigo-600" />
              เพิ่มโมเดล 3D เข้าสู่ระบบ
            </h3>

            <form onSubmit={handleUpload} className="space-y-3.5">
              {/* Category Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  ประเภทโมเดล (Category) <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => {
                    const isPicked = uploadCategory === c.id;
                    const IconComponent = c.icon;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleCategoryChange(c.id)}
                        className={`p-2 rounded-xl text-[11px] font-bold border text-left flex items-center gap-2 transition-all cursor-pointer ${
                          isPicked
                            ? 'border-indigo-600 bg-indigo-50/60 text-indigo-900 ring-1 ring-indigo-500'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                        }`}
                      >
                        <IconComponent size={14} style={{ color: c.color }} />
                        <span className="truncate">{c.label.split(' ')[1]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Model Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ชื่อโมเดล / รหัสรุ่น <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={modelName}
                  onChange={e => setModelName(e.target.value)}
                  placeholder="เช่น Toyota Reach Truck 8FBRE หรือ ช่างเทคนิค 170cm"
                  className="w-full p-2.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Target Height & Presets */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">
                    ความสูงเป้าหมายในฉาก 3D (เมตร)
                  </label>
                  <span className="text-[10px] text-indigo-600 font-bold">
                    {Math.round(parseFloat(targetHeightM || 0) * 100)} cm
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="10.0"
                    value={targetHeightM}
                    onChange={e => setTargetHeightM(e.target.value)}
                    className="w-24 p-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <div className="flex items-center gap-1 flex-1 overflow-x-auto">
                    {['0.30', '1.70', '2.10', '2.30', '3.00'].map(h => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setTargetHeightM(h)}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-colors cursor-pointer ${
                          targetHeightM === h ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {h}m
                      </button>
                    ))}
                  </div>
                </div>
                <span className="text-[10px] text-gray-400 mt-1 block">
                  Three.js จะปรับสเกลโมเดลอัตโนมัติให้ได้ความสูงนี้ ไม่ว่าไฟล์ต้นฉบับจะสเกลมาแบบใด
                </span>
              </div>

              {/* Description / Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  คำอธิบายเพิ่มเติม / สเปกโมเดล
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="เช่น ยกสูง 6m, รับน้ำหนัก 2 ตัน หรือ อุปกรณ์ประจำไลน์"
                  className="w-full p-2 border border-gray-300 rounded-xl text-xs font-medium text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* File Dropzone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ไฟล์โมเดล 3D (.glb, .gltf) <span className="text-red-500">*</span>
                </label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 hover:border-indigo-500 bg-gray-50 hover:bg-indigo-50/40 rounded-xl p-4 text-center cursor-pointer transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".glb,.gltf,.fbx,.obj"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {selectedFile ? (
                    <div className="space-y-1">
                      <div className="w-8 h-8 mx-auto bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
                        <FileText size={16} />
                      </div>
                      <div className="text-xs font-bold text-gray-800 truncate max-w-[220px] mx-auto">
                        {selectedFile.name}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {formatFileSize(selectedFile.size)}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1 text-gray-500">
                      <Box size={24} className="mx-auto text-gray-400" />
                      <div className="text-xs font-medium">คลิกเพื่อเลือกไฟล์ 3D</div>
                      <div className="text-[10px] text-gray-400">แนะนำไฟล์ .glb สำหรับ Three.js</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Alerts */}
              {uploadError && (
                <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center gap-1.5">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}
              {uploadSuccess && (
                <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-xl flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="shrink-0" />
                  <span>{uploadSuccess}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isUploading || !selectedFile}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-xs transition-all cursor-pointer"
              >
                {isUploading ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span>กำลังอัปโหลด...</span>
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    <span>อัปโหลดโมเดล 3D</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Quick Guide */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-slate-700 space-y-2">
            <div className="font-bold flex items-center gap-1.5 text-slate-900">
              <Sparkles size={14} className="text-amber-500" />
              การนำโมเดลไปใช้งานในหน้าวางผัง
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              เมื่ออัปโหลดโมเดลที่นี่แล้ว ให้ไปที่ <strong>ขั้นตอนที่ 1 (ผังและระบบจัดเก็บ)</strong> ในเมนูที่ 3 <strong>"รูปแบบแร็ค, ระบบจัดเก็บ & Area Design"</strong> เลือก <strong>"พื้นที่วางโมเดล 3D"</strong> จะมีเมนูให้เลือกโมเดลนี้ไปวางบนผังพร้อมหมุนทิศทางได้ทันที!
            </p>
          </div>
        </div>

        {/* Right Column: Model Asset Cards */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <Sliders size={16} className="text-indigo-600" />
              รายการโมเดล 3D ในระบบ ({filteredModels.length} รายการ)
            </h3>
            <span className="text-xs text-gray-400">
              หมวดหมู่: {CATEGORIES.find(c => c.id === selectedCategory)?.label}
            </span>
          </div>
          {/* Uploaded Models Grid */}
          <div className="space-y-3">

            {filteredModels.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400 space-y-2">
                <Box size={36} className="mx-auto text-gray-300" />
                <p className="text-xs font-bold text-gray-600">ยังไม่มีโมเดล 3D ที่อัปโหลดในหมวดนี้</p>
                <p className="text-[11px] text-gray-400">อัปโหลดไฟล์ .glb หรือ .gltf เพื่อนำมาแสดงผลในผัง 3D ได้ทันที</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredModels.map(m => {
                  const catInfo = getCategoryInfo(m.category);
                  const IconComp = catInfo.icon;
                  const isActive = m.is_active;

                  return (
                    <div
                      key={m.id}
                      className={`p-4 rounded-2xl border transition-all bg-white flex flex-col justify-between ${
                        isActive
                          ? 'border-indigo-500 shadow-sm ring-2 ring-indigo-100 bg-indigo-50/10'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="space-y-2.5">
                        {/* Title & Category Badge */}
                        <div className="flex items-start justify-between gap-2">
                          <div 
                            className="flex items-center gap-2.5 min-w-0 cursor-pointer group"
                            onClick={() => setPreviewModel(m)}
                            title="คลิกเพื่อดูตัวอย่าง 3D"
                          >
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 shadow-xs">
                              <IconComp size={20} style={{ color: catInfo.color }} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-extrabold text-gray-900 text-xs truncate group-hover:text-indigo-600 transition-colors flex items-center gap-1">
                                <span>{m.name}</span>
                                <Eye size={11} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <span className="text-[10px] text-gray-400 block truncate">
                                {m.file_name}
                              </span>
                            </div>
                          </div>

                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 border" style={{
                            backgroundColor: `${catInfo.color}15`,
                            color: catInfo.color,
                            borderColor: `${catInfo.color}30`
                          }}>
                            {catInfo.label.split(' ')[0]} {catInfo.label.split(' ')[1]}
                          </span>
                        </div>

                        {/* Description if any */}
                        {m.description && (
                          <p className="text-[11px] text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100 italic">
                            "{m.description}"
                          </p>
                        )}

                        {/* Specs Grid */}
                        <div className="grid grid-cols-2 gap-1.5 text-[11px] bg-slate-50/80 p-2 rounded-lg border border-slate-100 text-gray-600">
                          <div>
                            <span className="text-gray-400">ขนาดไฟล์:</span> <strong>{formatFileSize(m.file_size)}</strong>
                          </div>
                          <div>
                            <span className="text-gray-400">สเกลความสูง:</span> <strong className="text-indigo-700">{m.target_height_m || 1.70} ม.</strong>
                          </div>
                        </div>
                      </div>

                      {/* Action Bar */}
                      <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setPreviewModel(m)}
                            className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200/80 px-2.5 py-1 rounded-lg flex items-center gap-1.5 text-[11px] font-bold transition-all cursor-pointer shadow-xs active:scale-95"
                            title="เปิดพรีวิวดูโมเดล 3D แบบหมุน 360° และตรวจสอบสัดส่วนจริง"
                          >
                            <Eye size={13} />
                            <span>ดูตัวอย่าง 3D</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingModel(m)}
                            className="text-gray-500 hover:text-indigo-600 px-2 py-1 rounded-lg flex items-center gap-1 text-[11px] font-semibold transition-colors cursor-pointer hover:bg-gray-50"
                          >
                            <Edit3 size={12} />
                            <span>แก้ไข</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(m.id, m.name)}
                            className="text-red-500 hover:text-red-700 px-2 py-1 rounded-lg flex items-center gap-1 text-[11px] font-semibold transition-colors cursor-pointer hover:bg-red-50"
                          >
                            <Trash2 size={12} />
                            <span>ลบ</span>
                          </button>
                        </div>

                        {m.category === 'operator' && (
                          <div>
                            {!isActive ? (
                              <button
                                type="button"
                                onClick={() => handleSetActive(m.id)}
                                className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-[10px] transition-colors cursor-pointer"
                              >
                                ใช้ประจำสายพาน
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRevertToDefault(m.category)}
                                className="px-2 py-1 bg-emerald-100 text-emerald-800 font-bold rounded-lg text-[10px] cursor-pointer"
                              >
                                ✓ กำลังใช้ประจำสายพาน
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Model Modal */}
      {editingModel && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-gray-200 space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="font-bold text-sm text-gray-900 flex items-center gap-2">
                <Edit3 size={16} className="text-indigo-600" />
                แก้ไขข้อมูลโมเดล 3D
              </div>
              <button 
                type="button"
                onClick={() => setEditingModel(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ชื่อโมเดล / รุ่น
                </label>
                <input
                  type="text"
                  value={editingModel.name}
                  onChange={e => setEditingModel({ ...editingModel, name: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  หมวดหมู่ (Category)
                </label>
                <select
                  value={editingModel.category}
                  onChange={e => setEditingModel({ ...editingModel, category: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ความสูงเป้าหมายในฉาก 3D (เมตร)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.1"
                  max="10.0"
                  value={editingModel.target_height_m}
                  onChange={e => setEditingModel({ ...editingModel, target_height_m: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-xl text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  คำอธิบายเพิ่มเติม
                </label>
                <input
                  type="text"
                  value={editingModel.description || ''}
                  onChange={e => setEditingModel({ ...editingModel, description: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingModel(null)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {isSavingEdit && <RefreshCw size={12} className="animate-spin" />}
                  <span>บันทึกการเปลี่ยนแปลง</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3D Model Interactive Preview Modal */}
      {previewModel && (
        <Model3DPreviewModal
          model={previewModel}
          onClose={() => setPreviewModel(null)}
          onUpdateHeight={handleUpdateHeight}
        />
      )}
    </div>
  );
}
